import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Play, Film, User, LogOut, Menu, X, ArrowUpRight } from 'lucide-react';

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_d70cc816-e4d3-4f6e-bfaa-57b96c01f1a7/artifacts/w1f3hxo7_LTV-Production-LOGO-%281%29-white-2.png';

const Navbar = () => {
  const navigate = useNavigate();
  const [customer, setCustomer] = useState(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem('livestreamToken');
    if (token) {
      const customerData = localStorage.getItem('livestreamCustomer');
      if (customerData) {
        try { setCustomer(JSON.parse(customerData)); } catch (e) { /* ignore */ }
      }
    }
  }, []);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const handleSignOut = () => {
    localStorage.removeItem('livestreamToken');
    localStorage.removeItem('livestreamCustomer');
    setCustomer(null);
  };

  const goSignIn = () => {
    sessionStorage.setItem('homepageLoginRedirect', '/choose-destination');
    navigate('/live/login?redirect=/choose-destination');
    setMenuOpen(false);
  };

  const scrollTo = (id) => {
    setMenuOpen(false);
    if (window.location.pathname !== '/') {
      navigate('/');
      setTimeout(() => document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' }), 150);
    } else {
      document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const navLinks = [
    { label: 'Universe', action: () => scrollTo('ltv-universe') },
    { label: 'Services', action: () => scrollTo('our-services') },
    { label: 'Work', action: () => scrollTo('featured-work') },
    { label: 'Packages', action: () => scrollTo('production-packages') },
    { label: 'About', action: () => { setMenuOpen(false); navigate('/about'); } },
    { label: 'Contact', action: () => { setMenuOpen(false); navigate('/contact'); } },
  ];

  return (
    <header
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
        scrolled ? 'bg-[#050505]/85 backdrop-blur-2xl border-b border-white/[0.06]' : 'bg-transparent border-b border-transparent'
      }`}
    >
      <div className="max-w-[1400px] mx-auto px-5 sm:px-8">
        <div className="flex items-center justify-between h-16 sm:h-20">
          {/* Logo */}
          <img
            src={LOGO_URL}
            alt="LTV Production"
            className="h-7 sm:h-8 w-auto cursor-pointer"
            onClick={() => { setMenuOpen(false); navigate('/'); }}
          />

          {/* Desktop nav */}
          <nav className="hidden lg:flex items-center gap-8">
            {navLinks.map((link) => (
              <button
                key={link.label}
                onClick={link.action}
                className="text-[13px] font-medium tracking-wide text-gray-400 hover:text-white transition-colors"
              >
                {link.label}
              </button>
            ))}
          </nav>

          {/* Right actions */}
          <div className="flex items-center gap-2 sm:gap-3">
            <button
              onClick={() => navigate('/live')}
              className="hidden md:inline-flex items-center gap-2 text-[13px] font-semibold text-white bg-white/[0.06] hover:bg-white/[0.12] border border-white/10 rounded-full px-4 py-2 transition-all"
            >
              <span className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse" />
              LTV Live
            </button>
            <button
              onClick={() => navigate('/vod')}
              className="hidden md:inline-flex items-center gap-2 text-[13px] font-semibold text-white bg-white/[0.06] hover:bg-white/[0.12] border border-white/10 rounded-full px-4 py-2 transition-all"
            >
              <Film className="h-3.5 w-3.5 text-purple-400" />
              On Demand
            </button>

            {customer ? (
              <>
                <button
                  onClick={() => navigate('/choose-destination')}
                  className="inline-flex items-center gap-2 text-[13px] font-semibold text-black bg-white hover:bg-gray-200 rounded-full px-4 py-2 transition-all"
                >
                  <User className="h-3.5 w-3.5" />
                  <span className="hidden sm:inline max-w-[120px] truncate">
                    {customer.full_name || customer.email?.split('@')[0]}
                  </span>
                </button>
                <button
                  onClick={handleSignOut}
                  className="inline-flex items-center justify-center w-9 h-9 rounded-full bg-white/[0.06] hover:bg-white/[0.12] border border-white/10 text-white transition-all"
                  aria-label="Sign out"
                >
                  <LogOut className="h-3.5 w-3.5" />
                </button>
              </>
            ) : (
              <button
                onClick={goSignIn}
                className="inline-flex items-center gap-1.5 text-[13px] font-semibold text-black bg-white hover:bg-gray-200 rounded-full px-5 py-2 transition-all"
              >
                Sign In
                <ArrowUpRight className="h-3.5 w-3.5" />
              </button>
            )}

            {/* Mobile menu toggle */}
            <button
              onClick={() => setMenuOpen(!menuOpen)}
              className="lg:hidden inline-flex items-center justify-center w-9 h-9 rounded-full bg-white/[0.06] border border-white/10 text-white"
              aria-label="Menu"
            >
              {menuOpen ? <X className="h-4 w-4" /> : <Menu className="h-4 w-4" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile menu */}
      {menuOpen && (
        <div className="lg:hidden bg-[#050505]/97 backdrop-blur-2xl border-b border-white/[0.06]">
          <div className="px-5 py-6 flex flex-col gap-1">
            {navLinks.map((link) => (
              <button
                key={link.label}
                onClick={link.action}
                className="text-left text-base font-medium text-gray-300 hover:text-white py-3 border-b border-white/[0.05] transition-colors"
              >
                {link.label}
              </button>
            ))}
            <div className="flex gap-3 pt-5">
              <button
                onClick={() => { setMenuOpen(false); navigate('/live'); }}
                className="flex-1 inline-flex items-center justify-center gap-2 text-sm font-semibold text-white bg-red-600 hover:bg-red-500 rounded-full py-3 transition-all"
              >
                <Play className="h-4 w-4 fill-white" />
                LTV Live
              </button>
              <button
                onClick={() => { setMenuOpen(false); navigate('/vod'); }}
                className="flex-1 inline-flex items-center justify-center gap-2 text-sm font-semibold text-white bg-purple-600 hover:bg-purple-500 rounded-full py-3 transition-all"
              >
                <Film className="h-4 w-4" />
                On Demand
              </button>
            </div>
          </div>
        </div>
      )}
    </header>
  );
};

export default Navbar;
